import isNil from 'lodash/isNil';
import negate from 'lodash/negate';
import type {
	StartNodeLayout,
	Status,
	Transition,
	WorkflowErrorsMap,
	GlobalLoopsContainerLayout,
	Rule,
	Transitions,
	ScopeType,
} from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import {
	errorSeverity,
	SCOPE_GLOBAL,
	SCOPE_PROJECT,
	transitionType,
} from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';

import {
	type DiagramSharedTransition,
	type DiagramDirectTransition,
	type DiagramGlobalTransition,
	type Point,
	type DiagramStatus,
	ValidationFlag,
	TransitionIcons,
	type DiagramTransition,
	type ConditionalTransitionIcon,
	toPoint,
	type DiagramGlobalLoopedTransition,
} from './toPortInfo.tsx';

const CMP_APPROVAL_RULE_KEY = 'system:block-in-progress-approval';
const TMP_APPROVAL_APPROVE_RULE_KEY = 'system:jsd-approvals-block-until-approved';
const TMP_APPROVAL_DECLINE_RULE_KEY = 'system:jsd-approvals-block-until-rejected';
const TRIGGER_AGENT_RULE_KEY = 'system:trigger-agent';

type ConditionalTransitionIcons = ReadonlyArray<ConditionalTransitionIcon>;

type IconResolver = (s: Status[], t: Transition, r: Rule) => ConditionalTransitionIcons;

// Helper functions
// ------------------------------------------------------------------------------------------------

const CMP_ICON_CONFIG: [TransitionIcons, (t: Transition) => (s: Status) => boolean][] = [
	[
		TransitionIcons.Check,
		(t: Transition) =>
			({ approvalConfiguration }: Status) =>
				!!approvalConfiguration &&
				approvalConfiguration.active === 'true' &&
				approvalConfiguration.transitionApproved === t.id,
	],
	[
		TransitionIcons.Cross,
		(t: Transition) =>
			({ approvalConfiguration }: Status) =>
				!!approvalConfiguration &&
				approvalConfiguration.active === 'true' &&
				approvalConfiguration.transitionRejected === t.id,
	],
	[
		TransitionIcons.CheckInactive,
		(t: Transition) =>
			({ approvalConfiguration }: Status) =>
				!!approvalConfiguration &&
				!(approvalConfiguration.active === 'true') &&
				approvalConfiguration.transitionApproved === t.id,
	],
	[
		TransitionIcons.CrossInactive,
		(t: Transition) =>
			({ approvalConfiguration }: Status) =>
				!!approvalConfiguration &&
				!(approvalConfiguration.active === 'true') &&
				approvalConfiguration.transitionRejected === t.id,
	],
];

const TMP_ICON_CONFIG: [TransitionIcons, (r: Rule) => boolean][] = [
	[
		TransitionIcons.Check,
		({ config, ruleKey }: Rule): boolean =>
			ruleKey === TMP_APPROVAL_APPROVE_RULE_KEY &&
			!!config?.approvalConfigurationJson &&
			typeof config.approvalConfigurationJson === 'string' &&
			JSON.parse(config.approvalConfigurationJson).active,
	],
	[
		TransitionIcons.Cross,
		({ config, ruleKey }: Rule): boolean =>
			ruleKey === TMP_APPROVAL_DECLINE_RULE_KEY &&
			!!config?.approvalConfigurationJson &&
			typeof config.approvalConfigurationJson === 'string' &&
			JSON.parse(config.approvalConfigurationJson).active,
	],
	[
		TransitionIcons.CheckInactive,
		({ config, ruleKey }: Rule): boolean =>
			ruleKey === TMP_APPROVAL_APPROVE_RULE_KEY &&
			!!config?.approvalConfigurationJson &&
			typeof config.approvalConfigurationJson === 'string' &&
			!JSON.parse(config.approvalConfigurationJson).active,
	],
	[
		TransitionIcons.CrossInactive,
		({ config, ruleKey }: Rule): boolean =>
			ruleKey === TMP_APPROVAL_DECLINE_RULE_KEY &&
			!!config?.approvalConfigurationJson &&
			typeof config.approvalConfigurationJson === 'string' &&
			!JSON.parse(config.approvalConfigurationJson).active,
	],
];

const getCMPApprovalIcons: IconResolver = (statuses, transition) =>
	CMP_ICON_CONFIG.map(([type, statusFilter]) => ({
		type,
		fromStatuses: statuses.filter(statusFilter(transition)).map(({ id }) => id),
	})).filter(({ fromStatuses }) => (fromStatuses ?? []).length > 0);

const getTMPApprovalIcons: IconResolver = (statuses, { links }, rule) =>
	TMP_ICON_CONFIG.map(([type, ruleFilter]) => ({
		type,
		fromStatuses: ruleFilter(rule) ? (links ?? []).map(({ fromRef }) => fromRef) : [],
	})).filter(({ fromStatuses }) => (fromStatuses ?? []).length > 0);

const getApprovalIcons = (statuses: Status[], transition: Transition) => (rule: Rule) => {
	switch (rule.ruleKey) {
		case CMP_APPROVAL_RULE_KEY:
			return getCMPApprovalIcons(statuses, transition, rule);
		case TMP_APPROVAL_APPROVE_RULE_KEY:
		case TMP_APPROVAL_DECLINE_RULE_KEY:
			return getTMPApprovalIcons(statuses, transition, rule);
		default:
			return [];
	}
};

const isAnApprovalRule = ({ ruleKey }: Rule) => {
	const approvalRuleKeys = [
		CMP_APPROVAL_RULE_KEY,
		TMP_APPROVAL_APPROVE_RULE_KEY,
		TMP_APPROVAL_DECLINE_RULE_KEY,
	];

	return approvalRuleKeys.some((approvalKey) => ruleKey === approvalKey);
};

const isAnAgentRule = ({ ruleKey }: Rule) => {
	const agentRuleKeys = [TRIGGER_AGENT_RULE_KEY];

	return agentRuleKeys.some((approvalKey) => ruleKey === approvalKey);
};

/**
 * Merge icons.
 *
 * @param unmerged A list of conditional transition icons that may contain duplicate icons.
 * @returns A merged list of icons with only one entry per icon type.
 */
const mergeIcons = (unmerged: ConditionalTransitionIcons): ConditionalTransitionIcons => {
	// Take each icon and use its key as an index to a Map. Add each of its fromStatuses entries
	// to a Set to remove duplicates.
	const mergedMap = unmerged.reduce((iconMap, { type, fromStatuses }) => {
		// If this icon key isn't in the Map yet, add it along with an empty set as the value.
		if (!iconMap.has(type)) iconMap.set(type, new Set<string>());

		// Add each of the fromStatuses entries to the set.
		fromStatuses?.forEach((statusId) => iconMap.get(type)?.add(statusId));

		// Return the Map we just updated.
		return iconMap;
	}, new Map<TransitionIcons, Set<string>>());

	// Convert the map back to an array of ConditionalTransitionIcon objects.
	return [...mergedMap.entries()].map(([type, fromStatuses]) => ({
		type,
		...(fromStatuses.size > 0 ? { fromStatuses: [...fromStatuses.values()] } : {}),
	}));
};

//
// Exported functions
// ------------------------------------------------------------------------------------------------

export const toDiagramStartNodePosition = (startNodeLayout: StartNodeLayout): Point | undefined =>
	!isNil(startNodeLayout.x) && !isNil(startNodeLayout.y)
		? { x: startNodeLayout.x, y: startNodeLayout.y }
		: undefined;

export const toDiagramGlobalLoopsContainerPosition = (
	globalLoopsContainerLayout: GlobalLoopsContainerLayout | undefined,
): Point | undefined => {
	if (isNil(globalLoopsContainerLayout)) {
		return undefined;
	}

	if (isNil(globalLoopsContainerLayout.x) || isNil(globalLoopsContainerLayout.y)) {
		return undefined;
	}

	return toPoint(globalLoopsContainerLayout.x, globalLoopsContainerLayout.y);
};

export const toDiagramStatuses = (
	statuses: Status[],
	/**
	 * Make this a required prop when clean up sprt_hix_9246_wfe_diagram_complete_workflowscope
	 */
	workflowScope?: ScopeType,
	initialStatusId?: string,
	errors?: WorkflowErrorsMap,
): DiagramStatus[] =>
	statuses.map((status) => {
		const diagramStatus: DiagramStatus = {
			id: status.id,
			label: status.name,
			flag: getStatusValidationFlag(status, errors),
			position: status.layout,
			category: status.statusCategory,
			isInitial: status.id === initialStatusId,
			showGlobalScopeIndicator: workflowScope === SCOPE_PROJECT && status.scope === SCOPE_GLOBAL,
		};

		return diagramStatus;
	});

const getStatusValidationFlag = (status: Status, errors: WorkflowErrorsMap = {}) => {
	const statusErrors = errors?.[status.id]?.filter((e) => e.node === 'STATUS') || [];

	if (!statusErrors.length) {
		return undefined;
	}

	return statusErrors.some((error) => error.severity === errorSeverity.ERROR)
		? ValidationFlag.Error
		: ValidationFlag.Warn;
};

const getTransitionValidationFlag = (transition: Transition, errors: WorkflowErrorsMap = {}) => {
	const transitionsErrors = (
		errors[transition.id]?.filter((e) => e.node === 'TRANSITION') || []
	).concat(
		...transition.rules.filter((rule) => !isNil(errors[rule.id])).map((rule) => errors[rule.id]),
	);

	if (transitionsErrors.length) {
		return transitionsErrors.some((error) => error.severity === errorSeverity.ERROR)
			? ValidationFlag.Error
			: ValidationFlag.Warn;
	}
};

/**
 * To Diagram Transition Icons.
 *
 * Determine what icons should display on the transition label. Transition icons display under the
 * following conditions:
 *
 * | Icon                           | Reason                                |
 * | -------------------------------|---------------------------------------|
 * | **Error**                      | if there is an error or warning flag. |
 * | **Tick/Cross**                 | for an active approval                |
 * | **TickInactive/CrossInactive** | for an inactive approval              |
 * | **Bolt**                       | for any other transition rules        |
 * @param transition The transition.
 * @param availableRules A list of known rules that we consider valid.
 * @param flag Are there any warnings or errors on this transition?
 * @param statuses A list of statuses used in the Workflow (needed to look up approval
 *                 configuration)
 * @returns A list of icons to display on this transition label.
 */
const toDiagramTransitionIcons = (
	transition: Transition,
	flag: 'warn' | 'error' | undefined,
	statuses: Status[],
): ConditionalTransitionIcons => {
	const errorIcons = flag ? [{ type: TransitionIcons.Error }] : [];

	const approvalIcons = transition.rules
		.filter(isAnApprovalRule)
		.flatMap(getApprovalIcons(statuses, transition));

	const agentIcons = transition.rules.some(isAnAgentRule)
		? [{ type: TransitionIcons.TriggerAgent }]
		: [];

	// If the fg is on, we want to show the bolt icon for any rule that is not an
	// approval or agent rule
	const isNotAnApprovalRule = negate(isAnApprovalRule);
	const isNotAnAgentRule = negate(isAnAgentRule);
	const negateRules = (rule: Rule) => isNotAnApprovalRule(rule) && isNotAnAgentRule(rule);

	const otherIcons = transition.rules.some(negateRules) ? [{ type: TransitionIcons.Bolt }] : [];

	return mergeIcons([...errorIcons, ...otherIcons, ...approvalIcons, ...agentIcons]);
};

export const toDiagramTransitions = (
	transitions: Transitions,
	errors: WorkflowErrorsMap = {},
	statuses: Status[] = [],
): DiagramTransition[] =>
	transitions
		.filter((transition) => {
			return transition.type !== transitionType.DIRECTED || transition.links.length > 0;
		})
		.map((transition) => {
			const { id, type, links, name, toRef } = transition;

			const flag = getTransitionValidationFlag(transition, errors);
			const icons = toDiagramTransitionIcons(transition, flag, statuses);

			if (toRef == null) {
				const globalLoopedTransition: DiagramGlobalLoopedTransition = {
					id,
					type: 'global-looped',
					flag,
					icons,
					label: name,
				};
				return globalLoopedTransition;
			}

			if (type === transitionType.GLOBAL) {
				const globalTransition: DiagramGlobalTransition = {
					id,
					type: 'global',
					label: name,
					flag,
					icons,
					target: toRef,
				};
				return globalTransition;
			}

			if (links.length > 1) {
				const sharedTransition: DiagramSharedTransition = {
					id,
					type: 'shared',
					label: name,
					flag,
					icons,
					target: toRef,
					links: links.map((link) => ({
						source: link.fromRef,
						sourceIndex: link.fromPort,
						targetIndex: link.toPort,
					})),
				};

				return sharedTransition;
			}

			const directTransition: DiagramDirectTransition = {
				id,
				type: 'direct',
				label: name,
				flag,
				icons,
				target: toRef,
				targetIndex: links[0].toPort,
				source: links[0].fromRef,
				sourceIndex: links[0].fromPort,
			};

			return directTransition;
		});

export const getTransitionConnectionInfo = (
	transition: DiagramTransition,
): Pick<Transition, 'toRef' | 'links'> => {
	if (transition.type === 'shared') {
		return {
			toRef: transition.target,
			links: transition.links.map((link) => ({
				fromRef: link.source,
				fromPort: link.sourceIndex,
				toPort: link.targetIndex,
			})),
		};
	}

	if (transition.type === 'direct') {
		return {
			toRef: transition.target,
			links: [
				{
					fromRef: transition.source,
					fromPort: transition.sourceIndex,
					toPort: transition.targetIndex,
				},
			],
		};
	}

	if (transition.type === 'global-looped') {
		return {
			links: [],
		};
	}

	return { toRef: transition.target, links: [] };
};
